import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  LinearProgress,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Schedule,
  Warning,
} from "@mui/icons-material";
import api from "../../services/api";

// 税率タイプ
const TAX_TYPES = [
  { id: "standard", name: "標準税率", color: "primary" },
  { id: "reduced", name: "軽減税率", color: "success" },
];

const TaxScheduleSettings = () => {
  const { storeId } = useParams();
  const [schedules, setSchedules] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);

  const [formData, setFormData] = useState({
    taxType: "standard",
    taxRate: 10,
    effectiveFrom: "",
    effectiveTo: "",
  });

  const fetchSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/stores/${storeId}/tax-schedules`);
      setSchedules(response.data || []);
    } catch (err) {
      console.error("税率スケジュールの取得に失敗しました:", err);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handleOpenDialog = (schedule = null) => {
    if (schedule) {
      setEditingSchedule(schedule);
      setFormData({
        taxType: schedule.taxType || "standard",
        taxRate: schedule.taxRate || 10,
        effectiveFrom: schedule.effectiveFrom || "",
        effectiveTo: schedule.effectiveTo || "",
      });
    } else {
      setEditingSchedule(null);
      setFormData({
        taxType: "standard",
        taxRate: 10,
        effectiveFrom: "",
        effectiveTo: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSchedule(null);
  };

  const handleChange = (field) => (e) => {
    const value = field === "taxRate" ? parseFloat(e.target.value) : e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    // バリデーション
    if (!formData.effectiveFrom) {
      setError("適用開始日は必須です");
      return;
    }

    if (formData.effectiveTo && formData.effectiveFrom > formData.effectiveTo) {
      setError("適用終了日は開始日より後の日付を指定してください");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingSchedule) {
        await api.put(
          `/api/stores/${storeId}/tax-schedules/${editingSchedule.id}`,
          formData
        );
      } else {
        await api.post(`/api/stores/${storeId}/tax-schedules`, formData);
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      handleCloseDialog();
      fetchSchedules();
    } catch (err) {
      console.error("税率スケジュールの保存に失敗しました:", err);
      const message = err.response?.data?.message || "税率スケジュールの保存に失敗しました";
      // 期間重複エラーの場合は分かりやすいメッセージに
      if (message.includes("overlap") || message.includes("重複")) {
        setError("指定した期間は既存のスケジュールと重複しています。期間を調整してください。");
      } else {
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm("この税率スケジュールを削除しますか？")) return;

    try {
      await api.delete(`/api/stores/${storeId}/tax-schedules/${scheduleId}`);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      fetchSchedules();
    } catch (err) {
      console.error("税率スケジュールの削除に失敗しました:", err);
      setError("税率スケジュールの削除に失敗しました");
    }
  };

  const getTaxTypeChip = (type) => {
    const typeInfo = TAX_TYPES.find((t) => t.id === type) || TAX_TYPES[0];
    return (
      <Chip
        label={typeInfo.name}
        color={typeInfo.color}
        size="small"
      />
    );
  };

  const isCurrentlyActive = (schedule) => {
    const today = new Date().toISOString().split("T")[0];
    const from = schedule.effectiveFrom;
    const to = schedule.effectiveTo;

    if (from > today) return false;
    if (to && to < today) return false;
    return true;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "無期限";
    return new Date(dateStr).toLocaleDateString("ja-JP");
  };

  // 現在有効な税率
  const currentRates = {
    standard: schedules.find(
      (s) => s.taxType === "standard" && isCurrentlyActive(s)
    )?.taxRate,
    reduced: schedules.find(
      (s) => s.taxType === "reduced" && isCurrentlyActive(s)
    )?.taxRate,
  };

  return (
    <Box aria-busy={loading || saving}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Typography variant="h4" gutterBottom>
        税率スケジュール設定
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        税率の適用期間を設定します。将来の税率変更にも対応できます。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          処理が完了しました
        </Alert>
      )}

      {/* 現在の税率サマリー */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                現在の標準税率
              </Typography>
              <Typography variant="h3">
                {currentRates.standard !== undefined
                  ? `${currentRates.standard}%`
                  : "-"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                現在の軽減税率
              </Typography>
              <Typography variant="h3">
                {currentRates.reduced !== undefined
                  ? `${currentRates.reduced}%`
                  : "-"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* スケジュール一覧 */}
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Schedule color="primary" />
              <Typography variant="h6">税率スケジュール一覧</Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              スケジュール追加
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>状態</TableCell>
                  <TableCell>税率タイプ</TableCell>
                  <TableCell align="right">税率</TableCell>
                  <TableCell>適用開始日</TableCell>
                  <TableCell>適用終了日</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      税率スケジュールが登録されていません
                    </TableCell>
                  </TableRow>
                ) : (
                  schedules.map((schedule) => (
                    <TableRow
                      key={schedule.id}
                      sx={{
                        backgroundColor: isCurrentlyActive(schedule)
                          ? "action.selected"
                          : "inherit",
                      }}
                    >
                      <TableCell>
                        {isCurrentlyActive(schedule) ? (
                          <Chip label="有効" color="success" size="small" />
                        ) : schedule.effectiveFrom >
                          new Date().toISOString().split("T")[0] ? (
                          <Chip label="予定" color="info" size="small" />
                        ) : (
                          <Chip label="終了" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>{getTaxTypeChip(schedule.taxType)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="h6">{schedule.taxRate}%</Typography>
                      </TableCell>
                      <TableCell>{formatDate(schedule.effectiveFrom)}</TableCell>
                      <TableCell>{formatDate(schedule.effectiveTo)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(schedule)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(schedule.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 説明 */}
      <Alert severity="info" icon={<Warning />} sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>期間重複の防止</strong>
          <br />
          同じ税率タイプ（標準税率・軽減税率）で期間が重複するスケジュールは登録できません。
          将来の税率変更を登録する場合は、既存スケジュールの終了日を設定してから
          新しいスケジュールを追加してください。
        </Typography>
      </Alert>

      {/* スケジュール編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSchedule ? "税率スケジュールを編集" : "新規税率スケジュール"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>税率タイプ</InputLabel>
                <Select
                  value={formData.taxType}
                  onChange={handleChange("taxType")}
                  label="税率タイプ"
                >
                  {TAX_TYPES.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="税率 (%)"
                type="number"
                value={formData.taxRate}
                onChange={handleChange("taxRate")}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="適用開始日"
                type="date"
                value={formData.effectiveFrom}
                onChange={handleChange("effectiveFrom")}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="適用終了日（未入力で無期限）"
                type="date"
                value={formData.effectiveTo}
                onChange={handleChange("effectiveTo")}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !formData.effectiveFrom}
          >
            {saving ? <CircularProgress size={20} /> : "保存"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaxScheduleSettings;
